// frontend/src/hooks/useMasterData.ts
import { useState, useCallback } from 'react';
import { masterDataService, MasterRecord, SessionRecord, ClassRecord, SectionRecord } from '../services/master-data.service';

export interface MasterData {
  castes: MasterRecord[];
  religions: MasterRecord[];
  categories: MasterRecord[];
  bloodGroups: MasterRecord[];
  examBoards: MasterRecord[];
  houses: MasterRecord[];
  parentServiceTypes: MasterRecord[];
  familyRelations: MasterRecord[];
  documents: any[];
  nationalities: MasterRecord[];
  motherTongues: MasterRecord[];
  studentTypes: MasterRecord[];
  mediums: MasterRecord[];
  leavingReasons: MasterRecord[];
  academicSessions: SessionRecord[]; // Renamed from sessions
  classes: ClassRecord[];
}

export const useMasterData = () => {
  const [masters, setMasters] = useState<MasterData>({
    castes: [],
    religions: [],
    categories: [],
    bloodGroups: [],
    examBoards: [],
    houses: [],
    parentServiceTypes: [],
    familyRelations: [],
    documents: [],
    nationalities: [],
    motherTongues: [],
    studentTypes: [],
    mediums: [],
    leavingReasons: [],
    academicSessions: [],
    classes: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMasters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        castes,
        religions,
        categories,
        bloodGroups,
        examBoards,
        houses,
        parentServiceTypes,
        familyRelations,
        documents,
        nationalities,
        motherTongues,
        studentTypes,
        mediums,
        leavingReasons,
        allSessions, // Fetch all sessions
        classes
      ] = await Promise.all([
        masterDataService.getCastes(),
        masterDataService.getReligions(),
        masterDataService.getCategories(),
        masterDataService.getBloodGroups(),
        masterDataService.getExamBoards(),
        masterDataService.getHouses(),
        masterDataService.getParentServiceTypes(),
        masterDataService.getFamilyRelations(),
        masterDataService.getDocuments(),
        masterDataService.getNationalities(),
        masterDataService.getMotherTongues(),
        masterDataService.getStudentTypes(),
        masterDataService.getMediums(),
        masterDataService.getLeavingReasons(),
        masterDataService.getAllSessions(), // Use the new method
        masterDataService.getClasses()
      ]);

      setMasters({
        castes: Array.isArray(castes) ? castes : [],
        religions: Array.isArray(religions) ? religions : [],
        categories: Array.isArray(categories) ? categories : [],
        bloodGroups: Array.isArray(bloodGroups) ? bloodGroups : [],
        examBoards: Array.isArray(examBoards) ? examBoards : [],
        houses: Array.isArray(houses) ? houses : [],
        parentServiceTypes: Array.isArray(parentServiceTypes) ? parentServiceTypes : [],
        familyRelations: Array.isArray(familyRelations) ? familyRelations : [],
        documents: Array.isArray(documents) ? documents : [],
        nationalities: Array.isArray(nationalities) ? nationalities : [],
        motherTongues: Array.isArray(motherTongues) ? motherTongues : [],
        studentTypes: Array.isArray(studentTypes) ? studentTypes : [],
        mediums: Array.isArray(mediums) ? mediums : [],
        leavingReasons: Array.isArray(leavingReasons) ? leavingReasons : [],
        academicSessions: Array.isArray(allSessions) ? allSessions : [],
        classes: Array.isArray(classes) ? classes : [],
      });
    } catch (err: any) {
      console.error("Error fetching master data:", err);
      setError(err.message || "Failed to load master data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAcademicSessions = useCallback(async () => {
    try {
      const allSessions = await masterDataService.getAllSessions();
      setMasters((prev) => ({
        ...prev,
        academicSessions: Array.isArray(allSessions) ? allSessions : [],
      }));
    } catch (err) {
      console.error("Error fetching academic sessions:", err);
    }
  }, []);

  const fetchSections = useCallback(async (classId: string) => {
    try {
      return await masterDataService.getSections(classId);
    } catch (err) {
      console.error("Error fetching sections:", err);
      return [];
    }
  }, []);

  return { masters, loading, error, fetchMasters, fetchSections, fetchAcademicSessions };
};